import { AlertTriangle, ArrowRight, CheckCircle2, ExternalLink, HelpCircle, Info, RotateCcw, XCircle } from "lucide-react";
import { WhatsAppButton } from "../home/CtaButtons";
import { RVO_CONDITIONS_URL, eur, eurRate, nlDate, nlNum } from "../../lib/isde";
import { IsdeReviewRequest } from "./IsdeReviewRequest";

const STATUS = {
  mogelijk: { label: "A · Mogelijk subsidiabel", Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  aanvullend: { label: "B · Aanvullende gegevens nodig", Icon: HelpCircle, cls: "bg-amber-50 text-amber-800 ring-amber-200" },
  niet: { label: "C · Waarschijnlijk niet subsidiabel", Icon: XCircle, cls: "bg-rose-50 text-rose-800 ring-rose-200" },
};

const Row = ({ k, v, testid }) => (v || v === 0) && (
  <div className="flex justify-between gap-4 border-b border-white/10 py-2.5 text-[14.5px] last:border-0" data-testid={testid}>
    <dt className="text-slate-300">{k}</dt><dd className="text-right font-semibold text-white">{v}</dd>
  </div>
);

const Indication = ({ r }) => {
  const s = r.surfaces || {};
  const rates = r.lines.map((l) => `${eurRate(l.rate)}/m²`).join(" + ");
  return (
    <div className="relative overflow-hidden rounded-2xl bg-ink p-6 sm:p-7" data-testid="isde-indication">
      <div className="hero-grid-dark absolute inset-0 opacity-50" aria-hidden="true" />
      <div className="relative">
        <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#D9B97A]">Uw persoonlijke ISDE-indicatie</p>
        <p className="mt-2 font-display text-[clamp(2rem,4vw,2.75rem)] font-bold leading-none text-white" data-testid="isde-amount">{eur(r.amount)}</p>
        <p className="mt-1.5 text-[14px] text-slate-300">Voorlopige subsidie-indicatie{r.multiplier > 1 ? `, inclusief verdubbeling (×${r.multiplier})` : ""}</p>
        <dl className="mt-5">
          <Row k="Maatregel" v={r.lines.map((l) => l.name).join(" + ")} />
          <Row k="Toegepast regeljaar" v={r.year} testid="isde-result-year" />
          <Row k="Gebruikte regeling" v={r.rule_set && `${r.rule_set.name} (${r.rule_set.status_label.toLowerCase()})`} />
          <Row k="Tarief per m²" v={rates} testid="isde-result-rate" />
          {s.rounding_method === "rvo_totaal" && s.total_exact !== s.total_rounded && <Row k="Opgeteld zonder tussentijdse afronding" v={`${nlNum(s.total_exact)}\u00a0m² → afgerond ${nlNum(s.total_rounded)}\u00a0m²`} testid="isde-result-rounding" />}
          <Row k="Subsidiabel oppervlak (voorlopig)" v={`${nlNum(Math.min(r.total_m2, r.rule_set?.max_m2 ?? r.total_m2))}\u00a0m²`} testid="isde-result-m2" />
          {s.source === "maten" && <Row k="Ingevoerde kozijnmaten" v={`${nlNum(s.entered_m2)}\u00a0m²`} />}
          {s.estimated_glass_m2 > 0 && <Row k="Geschat glasoppervlak (alleen ter info)" v={`ca.\u00a0${nlNum(s.estimated_glass_m2)}\u00a0m²`} />}
          {s.excluded_m2 > 0 && <Row k="Niet meegerekend (nieuwe opening)" v={`${nlNum(s.excluded_m2)}\u00a0m²`} />}
          {r.capped && <Row k="Maximum" v={`Begrensd op ${nlNum(r.rule_set?.max_m2)}\u00a0m²`} />}
          {r.amount_with_combination && <Row k="Mogelijk met een tweede maatregel" v={`tot ${eur(r.amount_with_combination)}`} testid="isde-result-combination" />}
          {r.amount_with_transition && <Row k="Met overgangsregeling" v={eur(r.amount_with_transition)} />}
        </dl>
        {r.lines.length > 1 && (
          <ul className="mt-4 space-y-1 text-[13px] text-slate-300" data-testid="isde-result-lines">
            {r.lines.map((l) => <li key={l.key}>{l.name}: {nlNum(l.m2)} m² × {eurRate(l.rate)} = {eur(l.amount)}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
};

// Bij een combinatie zonder officieel bevestigde RVO-rekenwijze: ingevoerde oppervlakten tonen, geen bedrag.
const ReviewBlock = ({ r, prefill }) => {
  const s = r.surfaces;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 sm:p-5" data-testid="isde-review">
      <h3 className="text-[15px] font-semibold text-ink">Uw ingevoerde oppervlakten (zonder afronding)</h3>
      <ul className="mt-2 space-y-1 text-[14px] text-slate-700">
        {s.categories.map((c) => <li key={c.key} className="flex justify-between gap-4"><span>{c.name} ({eurRate(c.rate)}/m²)</span><span className="whitespace-nowrap font-semibold">{nlNum(c.m2_exact)}&nbsp;m²</span></li>)}
      </ul>
      <p className="mt-2 border-t border-amber-200 pt-2 text-[14px] text-slate-700" data-testid="isde-review-total">
        Samen <strong>{nlNum(s.total_exact)}&nbsp;m²</strong>. Afgerond als totaal: {nlNum(s.total_rounded_sum ?? s.total_rounded)}&nbsp;m²{s.total_rounded_parts != null && <>; afgerond per onderdeel: {nlNum(s.total_rounded_parts)}&nbsp;m²</>}.
        Welke rekenwijze RVO hier hanteert, is nog niet officieel bevestigd. Daarom noemen wij geen bedrag.
      </p>
      <IsdeReviewRequest key={r.calculation_id} result={r} prefill={prefill} />
    </div>
  );
};

const List = ({ title, items, Icon, tone = "text-slate-600", testid }) => items?.length > 0 && (
  <div data-testid={testid}>
    <h3 className="text-[15px] font-semibold text-ink">{title}</h3>
    <ul className="mt-2 space-y-2">
      {items.map((t) => <li key={t} className={`flex gap-2.5 text-[14.5px] leading-relaxed ${tone}`}><Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{t}</li>)}
    </ul>
  </div>
);

const Scenarios = ({ items, title }) => items?.length > 0 && (
  <div className="grid gap-3 sm:grid-cols-2" data-testid="isde-scenarios">
    {items.map((s) => (
      <div key={s.label} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-[12.5px] font-semibold uppercase tracking-[0.1em] text-slate-500">{title}</p>
        <p className="mt-1 text-[14px] text-ink">{s.label}</p>
        <p className="mt-1 font-display text-[22px] font-bold text-ink">{eur(s.amount)}</p>
        <p className="text-[12.5px] text-slate-500">{nlNum(s.total_m2)} m² · {s.lines.map((l) => `${eurRate(l.rate)}/m²`).join(" + ")}
          {s.amount_with_transition ? ` · met overgangsregeling ${eur(s.amount_with_transition)}` : ""}</p>
      </div>
    ))}
  </div>
);

const Sources = ({ r }) => (
  <div className="rounded-xl border border-slate-200 p-4 text-[13.5px] text-slate-600" data-testid="isde-sources">
    <p><strong className="text-ink">Bronnen:</strong>{" "}
      {(r.rule_set?.sources || [{ label: "RVO – ISDE: Isolatiemaatregelen woningeigenaren", url: RVO_CONDITIONS_URL }]).map((s, i) => (
        <span key={s.url}>{i > 0 && " · "}<a href={s.url} target="_blank" rel="noopener noreferrer" className="font-medium text-maas underline underline-offset-2">{s.label}</a></span>
      ))}
    </p>
    <p className="mt-1.5">{r.rule_set?.last_checked && <>Regels gecontroleerd op {nlDate(r.rule_set.last_checked)} · </>}Berekend op {nlDate(r.checked_on)} · Kenmerk {r.calculation_id}</p>
  </div>
);

export const IsdeResult = ({ result: r, onEdit, onQuote, prefill }) => {
  const st = r.review_needed ? { ...STATUS.aanvullend, label: "B · Persoonlijke controle noodzakelijk" } : STATUS[r.status];
  return (
    <div className="space-y-6" data-testid="isde-result" data-status={r.status}>
      <div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-semibold ring-1 ${st.cls}`} data-testid="isde-result-status"><st.Icon className="h-4 w-4" aria-hidden="true" /> {st.label}</span>
        <p className="mt-3 text-[17px] font-semibold leading-snug text-ink" data-testid="isde-result-headline">{r.headline}</p>
      </div>
      {r.amount !== null && r.amount !== undefined && <Indication r={r} />}
      <List title="Waarom?" items={r.reasons} Icon={XCircle} tone="text-rose-800" testid="isde-result-reasons" />
      <List title="Deze gegevens zijn nog nodig" items={r.missing} Icon={HelpCircle} tone="text-amber-800" testid="isde-result-missing" />
      <List title="Let op" items={r.blocks} Icon={AlertTriangle} tone="text-amber-800" testid="isde-result-blocks" />
      {r.review_needed && r.surfaces?.categories?.length > 0 && <ReviewBlock r={r} prefill={prefill} />}
      <Scenarios items={r.scenarios} title="Ter informatie" />
      {r.upcoming?.amount != null && (
        <div className="rounded-xl border border-gold/30 bg-gold-soft p-4 text-[14px] text-ink" data-testid="isde-upcoming">
          <p className="font-semibold">{r.upcoming.label}</p>
          <p className="mt-1">Indicatie dan: {eur(r.upcoming.amount)}{r.upcoming.amount_with_transition ? ` (met overgangsregeling ${eur(r.upcoming.amount_with_transition)})` : ""}. Aangekondigde regels zijn nog niet definitief.</p>
        </div>
      )}
      <List title="Goed om te weten" items={r.notes} Icon={Info} testid="isde-result-notes" />
      {r.assumptions?.length > 0 && (
        <details className="rounded-xl border border-slate-200 p-4" data-testid="isde-result-assumptions">
          <summary className="cursor-pointer text-[15px] font-semibold text-ink">Belangrijkste aannames ({r.assumptions.length})</summary>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[14px] leading-relaxed text-slate-600">{r.assumptions.map((t) => <li key={t}>{t}</li>)}</ul>
        </details>
      )}
      {r.referrals?.length > 0 && (
        <ul className="flex flex-wrap gap-x-5 gap-y-2" data-testid="isde-referrals">
          {r.referrals.map((x) => <li key={x.url}><a href={x.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[14px] font-semibold text-maas hover:underline">{x.label} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /></a></li>)}
        </ul>
      )}
      <Sources r={r} />
      <p className="text-[13px] leading-relaxed text-slate-500" data-testid="isde-disclaimer">{r.disclaimer}</p>
      <div className="flex flex-col gap-3 border-t border-slate-100 pt-5" data-testid="isde-result-ctas">
        <button type="button" onClick={onQuote} className="btn-cta group w-full sm:w-auto sm:self-start" data-testid="isde-quote-cta">
          Ontvang een vrijblijvende prijsindicatie voor uw nieuwe kozijnen <ArrowRight className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <a href={RVO_CONDITIONS_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="isde-rvo-cta">Bekijk de officiële voorwaarden <ExternalLink className="h-4 w-4" aria-hidden="true" /></a>
          <WhatsAppButton location="isde-result" />
          <button type="button" onClick={onEdit} className="btn-ghost" data-testid="isde-edit"><RotateCcw className="h-4 w-4" aria-hidden="true" /> Antwoorden aanpassen</button>
        </div>
      </div>
    </div>
  );
};
